import React from "react";
import { PersonFill, CalendarEvent, Clock } from "../icons";
import { Tag } from "lucide-react";
import "../../styles/booking-sidebar.scss";
import { useTranslation } from "react-i18next";

interface BookingInfoSidebarProps {
  className?: string;
  venueTitle?: string;
  /**
   * Absolute or protocol-relative URL of the venue's own first photo, used as
   * the banner above the booking summary. Omitted when the venue has no photo,
   * in which case no banner is rendered at all.
   */
  venueImageUrl?: string;
  bookingDate?: string;
  bookingTime?: string;
  covers?: string;
  bookingTypeName?: string;
  onChangeDetails?: () => void;
  isComplete?: boolean;
  minimal?: boolean;
}

const BookingInfoSidebar: React.FC<BookingInfoSidebarProps> = ({
  className = "",
  venueTitle,
  venueImageUrl,
  bookingDate,
  bookingTime,
  covers,
  bookingTypeName,
  onChangeDetails,
  isComplete = false,
  minimal = false,
}) => {
  const { t } = useTranslation();
  if (!isComplete) {
    return null;
  }

  return (
    <div className={`booking-info-sidebar gcw:bg-white gcw:lg:rounded-t-lg ${className}`}>
      {/* Venue banner, shown only when the venue actually has a photo.
          `alt` is intentionally empty: the venue name is rendered as text
          immediately below, so describing the image would make screen readers
          announce the venue twice. `object-cover` keeps the venue's own aspect
          ratio undistorted inside the fixed-height slot. The URL is used
          verbatim — see resolveVenueImageUrl for why assetUrl() must not be
          applied. `className` is deliberately not forwarded here: it belongs to
          the sidebar as a whole and a layout class would break the banner. */}
      {venueImageUrl && (
        <img
          src={venueImageUrl}
          alt=""
          className="gcw:w-full gcw:h-50 gcw:object-cover gcw:object-top gcw:lg:rounded-t-lg gcw:shadow-md"
        />
      )}
      {/* The card overlaps the banner by 100px on mobile, so the negative margin
          only applies when there is a banner to overlap.

          `relative z-10` is load-bearing, not decoration. The banner is an
          `img`, a replaced element, so its bitmap paints in CSS 2.1 Appendix E
          step 7 — after step 4, where this card's `bg-white` background paints.
          Without a positioned card the image would paint over the card and hide
          its top 100px. Only a positioned element (step 8) paints above replaced
          content; document order and `display: block` cannot. The pre-`img`
          design used a `background-image` div, whose background painted in
          step 4 in tree order, which is why it never needed this. */}
      <div
        className={`gcw:p-6 gcw:lg:p-0 ${venueImageUrl ? "gcw:-mt-[100px]" : ""} gcw:relative gcw:z-10 gcw:lg:mt-6 gcw:bg-white gcw:rounded-lg gcw:lg:rounded-none gcw:m-6 gcw:mb-0 gcw:lg:mb-6 gcw:justify-end gcw:shadow-lg gcw:lg:shadow-none`}
      >
        <div className="gcw:text-dark-grey gcw:text-lg gcw:font-semibold gcw:tracking-wide info gcw:mb-1">
          {t("booking_where_to_book", "Your booking at")}
        </div>
        <div className="gcw:text-3xl gcw:text-text-primary gcw:font-bold gcw:mb-4 venue-name">{venueTitle ?? ""}</div>
        {!minimal && (
          <>
            <div className="gcw:flex gcw:items-center gcw:gap-4 gcw:mb-2 gcw:text-dmn-dark gcw:text-xl gcw:font-semibold booking-info">
              <span className="gcw:flex gcw:items-center gcw:gap-2 ">
                <CalendarEvent size={20} className="gcw:text-dark-grey" aria-hidden="true" />
                {bookingDate ?? ""}
              </span>
              <span className="gcw:flex gcw:items-center gcw:gap-2">
                <PersonFill size={20} className="gcw:text-dark-grey" aria-hidden="true" />
                {covers ?? ""}
              </span>
              <span className="gcw:flex gcw:items-center gcw:gap-2">
                <Clock size={20} className="gcw:text-dark-grey" aria-hidden="true" />
                {bookingTime ?? ""}
              </span>
            </div>
            {bookingTypeName && (
              <div className="gcw:flex gcw:items-center gcw:gap-2 gcw:mb-4 gcw:text-dmn-dark gcw:text-xl gcw:font-semibold booking-type">
                <Tag size={20} className="gcw:text-dark-grey" aria-hidden="true" />
                {bookingTypeName}
              </div>
            )}
            {onChangeDetails && (
              <button
                type="button"
                onClick={onChangeDetails}
                className="gcw:text-text-brand-blue gcw:font-semibold gcw:text-body gcw:mt-4 gcw:block gcw:underline gcw:cursor-pointer gcw:bg-transparent gcw:border-none gcw:p-0"
              >
                {t("booking_change_details", "Change Details")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingInfoSidebar;
